package com.example.borshi.web.payment

import androidx.compose.runtime.*
import com.example.borshi.web.Screen
import com.example.borshi.web.data.WebOrder
import com.example.borshi.web.firebase.FirebaseService
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Composable
fun PaymentStatusScreen(
    orderId: String,
    source: String? = null,
    onNavigate: (Screen) -> Unit
) {
    val isWalletTopUp = source == "wallet"
    var isLoading by remember { mutableStateOf(true) }
    var order by remember { mutableStateOf<WebOrder?>(null) }
    var paymentStatus by remember { mutableStateOf<String?>(null) }
    var error by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(orderId) {
        if (orderId.isBlank()) {
            isLoading = false
            error = "No order ID found. This page is shown after payment with a link that includes your order ID. If you just completed payment, use the return link from the payment page, or go back to Home."
            return@LaunchedEffect
        }

        try {
            // Verify payment first
            val status = FirebaseService.verifyPayment(orderId)
            paymentStatus = status

            // Fallback: ensure wallet is credited for top-up orders (when webhook did not run)
            if (status == "PAID" || status == "SUCCESS") {
                FirebaseService.completeWalletTopUpIfPaid(orderId)
            }

            // Then fetch order details
            val orderDetails = FirebaseService.getOrder(orderId)
            order = orderDetails
        } catch (e: Throwable) {
            console.error("Error verifying payment:", e)
            error = e.message ?: "Failed to verify payment status"
        } finally {
            isLoading = false
        }
    }

    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            padding(24.px)
            maxWidth(600.px)
            property("margin", "0 auto")
            minHeight(60.vh)
            textAlign("center")
        }
    }) {
        if (isLoading) {
            H2 { Text("Verifying Payment...") }
            Div({
                style {
                    fontSize(48.px)
                    property("animation", "spin 1s linear infinite")
                }
            }) { Text("⏳") }
            P { Text("Please wait while we confirm your payment status.") }
        } else if (error != null) {
            H2({ style { color(Color.red) } }) { Text("Payment Verification Failed") }
            Div({ style { fontSize(64.px); marginBottom(16.px) } }) { Text("⚠️") }
            P { Text(error ?: "Unknown error") }
            
            Button({
                style {
                    marginTop(24.px)
                    padding(12.px, 24.px)
                    backgroundColor(Color("#D11243"))
                    color(Color.white)
                    border(0.px)
                    borderRadius(8.px)
                    cursor("pointer")
                }
                onClick { onNavigate(Screen.Home) }
            }) {
                Text("Back to Home")
            }
        } else {
            val isSuccess = paymentStatus == "PAID" || paymentStatus == "SUCCESS"
            
            if (isSuccess) {
                H2({ style { color(Color("#4CAF50")) } }) {
                    Text(if (isWalletTopUp) "Money Added to Wallet!" else "Payment Successful!")
                }
                Div({ style { fontSize(64.px); marginBottom(16.px) } }) { Text("✅") }
                P {
                    Text(if (isWalletTopUp) "Your wallet has been credited. You can use it for purchases." else "Thank you for your order. Your food will be delivered soon!")
                }
                
                order?.let {
                    Div({
                        style {
                            marginTop(24.px)
                            padding(16.px)
                            backgroundColor(Color.white)
                            borderRadius(8.px)
                            width(100.percent)
                            property("box-shadow", "0 2px 4px rgba(0,0,0,0.1)")
                            textAlign("left")
                        }
                    }) {
                        P { Text("Order ID: ${it.id}") }
                        P { Text("Amount: ₹${it.totalAmount}") }
                        P { Text("Status: ${it.status}") }
                        P { Text("Items: ${it.items.size}") }
                    }
                }

                Div({
                    style {
                        display(DisplayStyle.Flex)
                        gap(16.px)
                        marginTop(24.px)
                    }
                }) {
                    Button({
                        style {
                            padding(12.px, 24.px)
                            backgroundColor(Color("#D11243"))
                            color(Color.white)
                            border(0.px)
                            borderRadius(8.px)
                            cursor("pointer")
                        }
                        onClick { onNavigate(Screen.Account) }
                    }) {
                        Text(if (isWalletTopUp) "View Wallet" else "View My Orders")
                    }
                    
                    Button({
                        style {
                            padding(12.px, 24.px)
                            backgroundColor(Color.white)
                            color(Color("#D11243"))
                            border(1.px, LineStyle.Solid, Color("#D11243"))
                            borderRadius(8.px)
                            cursor("pointer")
                        }
                        onClick { onNavigate(Screen.Home) }
                    }) {
                        Text("Back to Home")
                    }
                }
            } else {
                H2({ style { color(Color("#F44336")) } }) { Text("Payment Pending or Failed") }
                Div({ style { fontSize(64.px); marginBottom(16.px) } }) { Text("❌") }
                P { Text("Status: ${paymentStatus ?: "Unknown"}") }
                P { Text("Please try again or contact support.") }
                
                Button({
                    style {
                        marginTop(24.px)
                        padding(12.px, 24.px)
                        backgroundColor(Color("#D11243"))
                        color(Color.white)
                        border(0.px)
                        borderRadius(8.px)
                        cursor("pointer")
                    }
                    onClick { if (isWalletTopUp) onNavigate(Screen.Account) else onNavigate(Screen.Cart) }
                }) {
                    Text(if (isWalletTopUp) "Back to Account" else "Return to Cart")
                }
            }
        }
    }
}

